/**
 * MinimizedProgressPill Component
 *
 * Compact progress indicator that shows when the panel is minimized.
 * Displays progress bar and count, expands to full panel on click.
 *
 * Pattern follows: RouteCalculationToast.tsx collapsed state
 */

import React from 'react';
import { ChevronUp, Loader2, Check, AlertCircle, XCircle } from 'lucide-react';
import type { EnrichmentOperation } from '../../store/enrichmentProgressStore';

interface MinimizedProgressPillProps {
  operation: EnrichmentOperation;
  onExpand: () => void;
}

/**
 * Get progress percentage
 */
const getProgressPercent = (current: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((current / total) * 100);
};

export const MinimizedProgressPill: React.FC<MinimizedProgressPillProps> = ({
  operation,
  onExpand,
}) => {
  const { status, currentIndex, totalCount } = operation;
  const progressPercent = getProgressPercent(currentIndex, totalCount);

  // Status-specific styles
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return 'border-green-500/50 bg-green-500/10';
      case 'failed':
        return 'border-red-500/50 bg-red-500/10';
      case 'cancelled':
        return 'border-yellow-500/50 bg-yellow-500/10';
      default:
        return 'border-brand-500/50 bg-brand-500/10';
    }
  };

  const getProgressBarColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-yellow-500';
      default:
        return 'bg-brand-500';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <Check className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Loader2 className="w-4 h-4 text-brand-400 animate-spin" />;
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'cancelled':
        return 'text-yellow-400';
      default:
        return 'text-brand-400';
    }
  };

  return (
    <button
      onClick={onExpand}
      className={`flex items-center gap-3 px-4 py-2 rounded-full border ${getStatusStyles()} shadow-lg hover:shadow-xl transition-all cursor-pointer min-w-[240px]`}
      aria-label="Expand enrichment progress"
    >
      {/* Status icon */}
      {getStatusIcon()}

      {/* Progress bar */}
      <div className="flex-1 h-2 bg-dark-surface/50 rounded-full overflow-hidden min-w-[100px]">
        <div
          className={`h-full transition-all duration-200 ease-out ${getProgressBarColor()}`}
          style={{ width: `${Math.min(progressPercent, 100)}%` }}
        />
      </div>

      {/* Progress text */}
      <span className={`text-xs font-medium ${getTextColor()} whitespace-nowrap`}>
        {currentIndex}/{totalCount} ({progressPercent}%)
      </span>

      {/* Expand icon */}
      <ChevronUp className="w-4 h-4 text-dark-muted" />
    </button>
  );
};

export default MinimizedProgressPill;
